// Lieferregeln und Gutscheine in Merxo (docs/integration/bestell-app.md §19, Stufe 2 der Verschmelzung). Merxo führt die
// Regeln – Zeitfenster für Lieferung und Abholung mit Kapazität, Ausnahmetage, Bestellschluss und Haltedauer, Liefergebiete mit
// Gebühr, Tragepauschale, „Lieferung frei“ je Geschäftskunde und Gutscheincodes –, die Bestell-App spiegelt sie und prüft im
// Checkout selbst. Merxo rechnet bei `sales.channel.submitOrder` verbindlich nach und legt jeden Verstoß als Prüfhinweis am
// Auftrag ab (nie eine Ablehnung, C5).
//
// Hier: die Verträge der Oberfläche (sales.checkout.*, sales.coupon.*; Rechte sales.checkout.read | write), die öffentlichen
// Verträge für die App (sales.channel.checkoutRules | slotLoad | checkCoupon, Recht sales.checkout.read) und das Ereignis
// sales.channel.checkoutRulesChanged. Für den KI-Assistenten: lesende Verträge 'default', schreibende 'opt-in' (nur nach
// Freigabe durch den Betrieb), die der App 'never'.
import { defineContract, defineEvent } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { ISSUE_CODES } from '../lib/checkout'
import { zDay, zTime } from './common'

const READ = 'sales.checkout.read'
const WRITE = 'sales.checkout.write'

const zText = (max: number) => z.string().trim().max(max)
const zKind = z.enum(['delivery', 'pickup'])
const zWeekday = z.int({ error: 'Bitte einen Wochentag von 1 (Montag) bis 7 (Sonntag) angeben.' }).min(1).max(7)
const zCents = (max = 100_000_000) => z.int({ error: 'Bitte einen Betrag in Cent angeben.' }).min(0, { error: 'Der Betrag darf nicht negativ sein.' }).max(max)
const zZip = z.string().trim().regex(/^\d{5}$/, { error: 'Eine Postleitzahl hat fünf Ziffern.' })
const zCouponCode = z
  .string({ error: 'Bitte einen Code angeben.' })
  .trim()
  .transform((value) => value.toUpperCase())
  .pipe(z.string().regex(/^[A-Z0-9-]{3,24}$/, { error: 'Der Code besteht aus 3 bis 24 Zeichen: Buchstaben, Ziffern und Bindestrich.' }))

// ---- Gemeinsame Formen -----------------------------------------------------------------------------------------------

export const zCheckoutIssue = z.object({
  code: z.enum(ISSUE_CODES),
  /** deutsch, für das Personal: „Fenster 10:00–12:00 am 17.10. ist voll (13 von 12).“ */
  message: z.string(),
  appCents: z.int().optional(),
  merxoCents: z.int().optional(),
})

const zSlot = z.object({ id: zId, weekday: zWeekday, start: zTime, end: zTime, capacity: z.int() })

export const zCheckoutZone = z.object({
  id: zId,
  name: z.string(),
  color: z.string(),
  zips: z.array(z.string()),
  feeCents: z.int(),
  minOrderCents: z.int(),
  freeFromCents: z.int().nullable(),
  position: z.int(),
  active: z.boolean(),
  /** Kartenmitte und Radius aus den bekannten Mittelpunkten der Postleitzahlen; null ohne bekannte Mittelpunkte */
  center: z.object({ lat: z.number(), lng: z.number() }).nullable(),
  radiusM: z.int().nullable(),
})

const zFreeCustomer = z.object({ customerId: zId, number: z.string().nullable(), name: z.string(), note: z.string().nullable() })

/** Alle Regeln eines Standorts (dazu die mandantenweiten: Tragepauschale und „Lieferung frei“). */
export const zCheckoutRules = z.object({
  location: z.object({ id: zId, code: z.string(), name: z.string() }),
  /** false: die Lieferregeln sind noch nicht eingerichtet – Merxo prüft dann keine Bestellung nach */
  configured: z.boolean(),
  deliverySlots: z.array(zSlot),
  pickupSlots: z.array(zSlot),
  exceptions: z.array(z.object({ id: zId, day: z.string(), kind: z.enum(['delivery', 'pickup', 'both']), note: z.string().nullable() })),
  deliveryCutoffMinutes: z.int(),
  pickupCutoffMinutes: z.int(),
  pickupHoldHours: z.int(),
  carryFeeCents: z.int(),
  zones: z.array(zCheckoutZone),
  freeDelivery: z.array(zFreeCustomer),
  /** Hash über den Inhalt (wie `sales.channel.checkoutRules`) */
  version: z.string(),
})

// ---- Lesen ------------------------------------------------------------------------------------------------------------

export const checkoutGet = defineContract({
  name: 'sales.checkout.get',
  title: 'Lieferregeln eines Standorts anzeigen',
  description: 'Zeitfenster, Ausnahmetage, Bestellschluss, Liefergebiete, Tragepauschale und „Lieferung frei“ eines Standorts.',
  llm: {
    description:
      'Liefert die Lieferregeln eines Ausgangsstandorts: Wochenvorlagen der Zeitfenster für Lieferung und Abholung mit Kapazität ' +
      '(Wochentag 1 = Montag … 7 = Sonntag), Ausnahmetage, Bestellschluss in Minuten vor Fensterbeginn, Haltedauer der Abholung, ' +
      'Liefergebiete mit Postleitzahlen, Gebühr, Mindestbestellwert und „frei ab“, die Tragepauschale und die Kunden mit ' +
      '„Lieferung frei“. Der Vertriebskanal spiegelt diese Regeln; Gutscheine: sales.coupon.list.',
    keywords: ['Lieferregeln', 'Zeitfenster', 'Liefergebiete', 'Lieferkosten', 'Bestellschluss', 'Tragepauschale', 'Ausnahmetage'],
  },
  input: z.strictObject({ locationId: zId }),
  output: zCheckoutRules,
  permission: READ,
  risk: 'read',
  scope: (input) => [input.locationId],
})

export const checkoutLoad = defineContract({
  name: 'sales.checkout.load',
  title: 'Belegung der Zeitfenster anzeigen',
  description: 'Gebuchte Aufträge je Tag und Fenster eines Standorts – für die nächsten Tage.',
  llm: {
    description:
      'Liefert die Belegung der Zeitfenster eines Standorts für `days` Tage ab `from`: je Tag die Fenster mit Kapazität, gebuchten ' +
      'Aufträgen (alle nicht stornierten Aufträge mit dieser Lieferart, diesem Liefertermin und genau diesem Fenster – gleich welcher ' +
      'Herkunft), freien Plätzen und Zustand (frei, fast voll, voll, überbucht, gesperrt). Ausnahmetage stehen als geschlossen da.',
    keywords: ['Belegung', 'Auslastung', 'Fenster voll', 'ausgebucht', 'freie Fenster'],
  },
  input: z.strictObject({ locationId: zId, kind: zKind, from: zDay.optional(), days: z.int().min(1).max(14).default(7) }),
  output: z.object({
    kind: zKind,
    days: z.array(
      z.object({
        day: z.string(),
        closed: z.object({ note: z.string().nullable() }).nullable(),
        slots: z.array(
          z.object({
            start: zTime,
            end: zTime,
            capacity: z.int(),
            booked: z.int(),
            free: z.int(),
            /** free = Platz · almost = ein Platz oder weniger als 20 % · full = voll · over = überbucht · blocked = gesperrt (Kapazität 0) · adhoc = Fenster ohne Vorlage */
            state: z.enum(['free', 'almost', 'full', 'over', 'blocked', 'adhoc']),
          }),
        ),
      }),
    ),
  }),
  permission: READ,
  risk: 'read',
  scope: (input) => [input.locationId],
})

// ---- Schreiben (ganze Liste eines Standorts) -----------------------------------------------------------------------

export const checkoutSaveSlots = defineContract({
  name: 'sales.checkout.saveSlots',
  title: 'Zeitfenster eines Standorts speichern',
  description: 'Ersetzt die Wochenvorlagen der Fenster für Lieferung oder Abholung eines Standorts.',
  llm: {
    description:
      'Speichert die vollständige Liste der Wochenvorlagen einer Art (`delivery` oder `pickup`) für einen Standort: Wochentag ' +
      '(1 = Montag … 7 = Sonntag), Beginn und Ende (HH:MM) und Kapazität (Aufträge je Fenster, 0 = gesperrt). Fenster ' +
      'desselben Wochentags dürfen sich nicht überschneiden. Was nicht in der Liste steht, entfällt. Der Vertriebskanal erfährt ' +
      'die Änderung sofort.',
    keywords: ['Zeitfenster ändern', 'Lieferfenster', 'Abholfenster', 'Kapazität ändern'],
  },
  input: z.strictObject({
    locationId: zId,
    kind: zKind,
    slots: z
      .array(z.strictObject({ weekday: zWeekday, start: zTime, end: zTime, capacity: z.int({ error: 'Bitte eine Kapazität angeben.' }).min(0, { error: 'Die Kapazität liegt zwischen 0 und 999.' }).max(999, { error: 'Die Kapazität liegt zwischen 0 und 999.' }) }))
      .max(200),
  }),
  output: zCheckoutRules,
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: (input) => [input.locationId],
  effects: { reversible: true },
  limits: { maxItems: 200, itemCount: (input) => input.slots.length },
})

export const checkoutSaveExceptions = defineContract({
  name: 'sales.checkout.saveExceptions',
  title: 'Ausnahmetage eines Standorts speichern',
  description: 'Ersetzt die Ausnahmetage (geschlossen) eines Standorts.',
  llm: {
    description:
      'Speichert die vollständige Liste der Ausnahmetage eines Standorts: Datum, Art (`delivery`, `pickup` oder `both`) und Hinweis ' +
      '(z. B. „Allerheiligen“). An Ausnahmetagen gibt es keine Fenster. Was nicht in der Liste steht, entfällt.',
    keywords: ['Ausnahmetag', 'Feiertag', 'geschlossen', 'Betriebsferien'],
  },
  input: z.strictObject({
    locationId: zId,
    exceptions: z.array(z.strictObject({ day: zDay, kind: z.enum(['delivery', 'pickup', 'both']), note: zText(200).nullish() })).max(400),
  }),
  output: zCheckoutRules,
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: (input) => [input.locationId],
  effects: { reversible: true },
  limits: { maxItems: 400, itemCount: (input) => input.exceptions.length },
})

export const checkoutSaveSettings = defineContract({
  name: 'sales.checkout.saveSettings',
  title: 'Bestellschluss, Haltedauer und Tragepauschale speichern',
  description: 'Legt Bestellschluss und Haltedauer eines Standorts und die Tragepauschale fest.',
  llm: {
    description:
      'Speichert für einen Standort den Bestellschluss in Minuten vor Fensterbeginn (Lieferung, Abholung) und die Haltedauer ' +
      'reservierter Abholungen in Stunden; optional die Tragepauschale (Cent brutto je Bestellung, gilt für den ganzen ' +
      'Mandanten, 0 = nicht angeboten).',
    keywords: ['Bestellschluss', 'Haltedauer', 'Tragepauschale', 'Vorlaufzeit'],
  },
  input: z.strictObject({
    locationId: zId,
    deliveryCutoffMinutes: z.int({ error: 'Bitte Minuten angeben.' }).min(0).max(1440),
    pickupCutoffMinutes: z.int({ error: 'Bitte Minuten angeben.' }).min(0).max(1440),
    pickupHoldHours: z.int({ error: 'Bitte Stunden angeben.' }).min(0).max(720),
    carryFeeCents: zCents(100_000).optional(),
  }),
  output: zCheckoutRules,
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: (input) => [input.locationId],
  effects: { reversible: true },
})

export const checkoutSaveZones = defineContract({
  name: 'sales.checkout.saveZones',
  title: 'Liefergebiete eines Standorts speichern',
  description: 'Ersetzt die Liefergebiete eines Standorts: Postleitzahlen, Gebühr, Mindestbestellwert, „frei ab“.',
  llm: {
    description:
      'Speichert die vollständige Liste der Liefergebiete eines Standorts in ihrer Reihenfolge: Name, Farbe (#rrggbb), ' +
      'Postleitzahlen (fünf Ziffern), Gebühr brutto, Mindestbestellwert und „frei ab“ (Warenwert ohne Nebenposten; leer = nie frei) in ' +
      'Cent. Eine Postleitzahl gehört je Standort höchstens zu einem aktiven Gebiet. Gebiete mit `id` werden geändert, ohne `id` ' +
      'neu angelegt, fehlende entfallen.',
    keywords: ['Liefergebiet', 'Postleitzahl', 'Liefergebühr', 'Mindestbestellwert', 'Lieferkosten'],
  },
  input: z.strictObject({
    locationId: zId,
    zones: z
      .array(
        z.strictObject({
          id: zId.optional(),
          name: zText(80).min(1, { error: 'Bitte einen Namen angeben.' }),
          color: z.string().trim().regex(/^#[0-9a-fA-F]{6}$/, { error: 'Bitte eine Farbe als #rrggbb angeben.' }).default('#1d58a0'),
          zips: z.array(zZip).max(300),
          feeCents: zCents(1_000_000),
          minOrderCents: zCents(),
          freeFromCents: zCents().nullable(),
          active: z.boolean().default(true),
        }),
      )
      .max(50),
  }),
  output: zCheckoutRules,
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: (input) => [input.locationId],
  effects: { reversible: true },
  limits: { maxItems: 50, itemCount: (input) => input.zones.length },
})

export const checkoutSaveFreeDelivery = defineContract({
  name: 'sales.checkout.saveFreeDelivery',
  title: '„Lieferung frei“ für Kunden speichern',
  description: 'Ersetzt die Liste der Geschäftskunden, die keine Liefergebühr zahlen (gilt für den ganzen Mandanten).',
  llm: {
    description:
      'Speichert die vollständige Liste der Kunden mit „Lieferung frei“: Sie zahlen keine Liefergebühr, den Mindestbestellwert ' +
      'müssen sie trotzdem erreichen. Gilt für alle Standorte. Was nicht in der Liste steht, entfällt.',
    keywords: ['Lieferung frei', 'Liefergebühr erlassen', 'Stammkunden', 'Geschäftskunden'],
  },
  input: z.strictObject({ customers: z.array(z.strictObject({ customerId: zId, note: zText(200).nullish() })).max(500) }),
  output: z.object({ customers: z.array(zFreeCustomer) }),
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: () => [],
  effects: { reversible: true },
  limits: { maxItems: 500, itemCount: (input) => input.customers.length },
})

// ---- Gutscheine -------------------------------------------------------------------------------------------------------

export const COUPON_STATES = ['all', 'active', 'inactive', 'expired'] as const

export const zCoupon = z.object({
  id: zId,
  code: z.string(),
  kind: z.enum(['percent', 'fixed']),
  /** Prozent (1–100) bzw. Cent */
  value: z.int(),
  minOrderCents: z.int().nullable(),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  b2cOnly: z.boolean(),
  active: z.boolean(),
  maxRedemptions: z.int().nullable(),
  maxPerCustomer: z.int().nullable(),
  note: z.string().nullable(),
  /** aktive Einlösungen */
  redeemed: z.int(),
  /** frei, aktiv, ausgeschöpft, abgelaufen, noch nicht gültig, inaktiv – für die Anzeige */
  state: z.enum(['active', 'inactive', 'expired', 'upcoming', 'exhausted']),
  stateLabel: z.string(),
  /** Code und Art sind nach der ersten Einlösung fest */
  locked: z.boolean(),
  version: z.int(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export const couponList = defineContract({
  name: 'sales.coupon.list',
  title: 'Gutscheine anzeigen',
  description: 'Alle Gutscheincodes mit Wert, Gültigkeit, Einlösungen und Zustand.',
  llm: {
    description: 'Listet die Gutscheincodes des Mandanten: Art und Wert, Mindestbestellwert, Gültigkeit, Grenzen, Einlösungen (aktive) und Zustand. `state` und `q` (Code oder Notiz) schränken ein.',
    keywords: ['Gutschein', 'Rabattcode', 'Gutscheincode', 'Aktionscode'],
  },
  input: z.strictObject({ q: zText(60).optional(), state: z.enum(COUPON_STATES).default('all') }),
  output: z.object({ rows: z.array(zCoupon) }),
  permission: READ,
  risk: 'read',
  scope: () => [],
})

export const couponGet = defineContract({
  name: 'sales.coupon.get',
  title: 'Gutschein anzeigen',
  description: 'Ein Gutschein mit allen Angaben.',
  llm: { description: 'Liefert einen Gutschein (Id) mit Art, Wert, Gültigkeit, Grenzen, Einlösungen und Zustand.', keywords: ['Gutschein', 'Rabattcode'] },
  input: z.strictObject({ id: zId }),
  output: zCoupon,
  permission: READ,
  risk: 'read',
  scope: () => [],
})

const couponFields = {
  kind: z.enum(['percent', 'fixed']),
  value: z.int({ error: 'Bitte einen Wert angeben.' }).min(1, { error: 'Der Wert muss größer als 0 sein.' }).max(10_000_000),
  minOrderCents: zCents().nullish(),
  validFrom: zDay.nullish(),
  validUntil: zDay.nullish(),
  b2cOnly: z.boolean().default(false),
  maxRedemptions: z.int({ error: 'Bitte eine ganze Zahl angeben.' }).min(1, { error: 'Mindestens 1 – oder leer für „unbegrenzt“.' }).max(1_000_000).nullish(),
  maxPerCustomer: z.int({ error: 'Bitte eine ganze Zahl angeben.' }).min(1, { error: 'Mindestens 1 – oder leer für „unbegrenzt“.' }).max(10_000).nullish(),
  note: zText(500).nullish(),
}

const percentValueRule = (input: { kind: 'percent' | 'fixed'; value: number }) => input.kind !== 'percent' || input.value <= 100
const validRangeRule = (input: { validFrom?: string | null | undefined; validUntil?: string | null | undefined }) => !input.validFrom || !input.validUntil || input.validUntil >= input.validFrom

export const couponCreate = defineContract({
  name: 'sales.coupon.create',
  title: 'Gutschein anlegen',
  description: 'Legt einen Gutscheincode an (Prozent oder fester Betrag).',
  llm: {
    description:
      'Legt einen Rabattcode an: Code (3–24 Zeichen, Buchstaben, Ziffern, Bindestrich; ohne Unterschied zwischen Groß- und Kleinschreibung, ' +
      'eindeutig), Art `percent` (1–100 %) oder `fixed` (Cent), Mindestbestellwert, gültig von/bis, nur Privatkunden, Einlösungen ' +
      'gesamt und je Kunde (leer = unbegrenzt). Der Rabatt gilt auf den Warenwert ohne Nebenposten.',
    keywords: ['Gutschein anlegen', 'Rabattcode', 'Aktionscode', 'Gutscheincode'],
  },
  input: z.strictObject({ code: zCouponCode, ...couponFields, active: z.boolean().default(true) }).refine(percentValueRule, { error: 'Ein Prozentwert liegt zwischen 1 und 100.', path: ['value'] }).refine(validRangeRule, { error: 'Das Ende liegt vor dem Beginn.', path: ['validUntil'] }),
  output: zCoupon,
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: () => [],
  effects: { reversible: true },
})

export const couponUpdate = defineContract({
  name: 'sales.coupon.update',
  title: 'Gutschein ändern',
  description: 'Ändert Wert, Gültigkeit und Grenzen eines Gutscheins; Code und Art nur vor der ersten Einlösung.',
  llm: {
    description:
      'Ändert einen Gutschein (Id): Wert, Mindestbestellwert, Gültigkeit, Grenzen, Notiz – und vor der ersten Einlösung auch Code und Art. ' +
      '`expectedVersion` schützt vor gleichzeitigen Änderungen. Bereits gebuchte Einlösungen bleiben, wie sie sind.',
    keywords: ['Gutschein ändern', 'Gültigkeit verlängern', 'Rabattcode ändern'],
  },
  input: z.strictObject({ id: zId, expectedVersion: z.int().optional(), code: zCouponCode, ...couponFields }).refine(percentValueRule, { error: 'Ein Prozentwert liegt zwischen 1 und 100.', path: ['value'] }).refine(validRangeRule, { error: 'Das Ende liegt vor dem Beginn.', path: ['validUntil'] }),
  output: zCoupon,
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: () => [],
  effects: { reversible: true },
})

export const couponSetActive = defineContract({
  name: 'sales.coupon.setActive',
  title: 'Gutschein aktivieren oder sperren',
  description: 'Schaltet einen Gutscheincode ein oder aus.',
  llm: { description: 'Aktiviert oder sperrt einen Gutschein (Id). Ein gesperrter Code gilt nicht mehr; gebuchte Einlösungen bleiben.', keywords: ['Gutschein sperren', 'Gutschein aktivieren', 'Rabattcode beenden'] },
  input: z.strictObject({ id: zId, active: z.boolean() }),
  output: zCoupon,
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: () => [],
  effects: { reversible: true },
})

export const couponRedemptions = defineContract({
  name: 'sales.coupon.redemptions',
  title: 'Einlösungen eines Gutscheins anzeigen',
  description: 'Alle Einlösungen eines Gutscheins mit Auftrag, Kunde und Rabatt.',
  llm: { description: 'Listet die Einlösungen eines Gutscheins (Id), neueste zuerst: Auftrag, Kunde, Rabatt, Zustand (aktiv oder freigegeben) und Markierungen (über der Grenze).', keywords: ['Einlösungen', 'Gutschein verwendet', 'wer hat den Gutschein benutzt'] },
  input: z.strictObject({ id: zId }),
  output: z.object({
    rows: z.array(
      z.object({
        id: zId,
        orderId: zId,
        orderNumber: z.string().nullable(),
        externalNumber: z.string().nullable(),
        orderStatusLabel: z.string(),
        customer: z.string().nullable(),
        discountCents: z.int(),
        status: z.enum(['active', 'released']),
        flags: z.array(z.enum(['over_limit', 'over_customer_limit'])),
        createdAt: z.date(),
        releasedAt: z.date().nullable(),
      }),
    ),
  }),
  permission: READ,
  risk: 'read',
  scope: () => [],
})

// ---- Öffentliche Verträge für die App (§19.3) ----------------------------------------------------------------------

const zHm = zTime
const zChannelSlot = z.object({ id: zId, weekday: zWeekday, start: zHm, end: zHm, capacity: z.int() })

export const channelCheckoutRules = defineContract({
  name: 'sales.channel.checkoutRules',
  title: 'Lieferregeln für einen Vertriebskanal abrufen',
  description: 'Alle Regeln für den Spiegel der Bestell-App: Zeitfenster, Ausnahmetage, Liefergebiete, Tragepauschale, „Lieferung frei“ und Gutscheine.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Liefert je Standort mit Leserecht die Wochenvorlagen der Fenster, die Ausnahmetage ' +
      '(heute bis +60 Tage), Bestellschluss und Haltedauer und die Liefergebiete, dazu die Tragepauschale, die Kundennummern ' +
      'mit „Lieferung frei“ und die aktiven Gutscheine. `version` ist ein Hash über den Inhalt: gleich = nichts zu tun.',
    keywords: ['Lieferregeln', 'Zeitfenster', 'Liefergebiete', 'Gutscheine', 'Bestell-App', 'Schnittstelle', 'Spiegel'],
  },
  input: z.strictObject({}),
  output: z.object({
    version: z.string(),
    serverTime: z.date(),
    locations: z.array(
      z.object({
        code: z.string(),
        deliverySlots: z.array(zChannelSlot),
        pickupSlots: z.array(zChannelSlot),
        exceptions: z.array(z.object({ day: zDay, kind: z.enum(['delivery', 'pickup', 'both']), note: z.string().nullable() })),
        deliveryCutoffMinutes: z.int(),
        pickupCutoffMinutes: z.int(),
        pickupHoldHours: z.int(),
        zones: z.array(
          z.object({
            id: zId,
            name: z.string(),
            color: z.string(),
            zips: z.array(z.string()),
            feeCents: z.int(),
            minOrderCents: z.int(),
            freeFromCents: z.int().nullable(),
            center: z.object({ lat: z.number(), lng: z.number() }).nullable(),
            radiusM: z.int().nullable(),
          }),
        ),
        /**
         * Marktdaten des Standorts (Stufe 3, §20.3 S3): Öffnungszeiten im Format der App (Wochentag '0' = Sonntag … '6' = Samstag →
         * { open, close } oder null) und der Hinweis (sichtbar von/bis als Tag, einschließlich). null = in Merxo nicht eingerichtet:
         * Die App behält ihre eigenen Werte.
         */
        store: z
          .object({
            openingHours: z.record(z.string(), z.object({ open: zHm, close: zHm }).nullable()),
            announcement: z.object({ text: z.string(), kind: z.enum(['info', 'warning']), from: z.string().nullable(), until: z.string().nullable() }).nullable(),
          })
          .nullable(),
      }),
    ),
    carryFeeCents: z.int(),
    freeDeliveryCustomerNumbers: z.array(z.string()),
    coupons: z.array(
      z.object({
        id: zId,
        code: z.string(),
        kind: z.enum(['percent', 'fixed']),
        value: z.int(),
        minOrderCents: z.int().nullable(),
        validFrom: z.string().nullable(),
        validUntil: z.string().nullable(),
        b2cOnly: z.boolean(),
        maxRedemptions: z.int().nullable(),
        maxPerCustomer: z.int().nullable(),
        redeemed: z.int(),
      }),
    ),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
  publicApi: true,
  assistant: 'never',
})

export const channelSlotLoad = defineContract({
  name: 'sales.channel.slotLoad',
  title: 'Belegung der Zeitfenster für einen Vertriebskanal abrufen',
  description: 'Gebuchte Aufträge je Tag und Fenster eines Standorts – live, gleich welcher Herkunft.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Liefert für `locationCode` und die Art (`delivery` oder `pickup`) von … bis … ' +
      '(höchstens 14 Tage) je Fenster die Zahl der gebuchten Aufträge: alle nicht stornierten Aufträge mit dieser Lieferart, diesem Standort, ' +
      'diesem Liefertermin und genau diesem Fenster – auch Fenster ohne Vorlage (z. B. ein Telefonauftrag mit eigenem Fenster).',
    keywords: ['Belegung', 'Zeitfenster', 'ausgebucht', 'Bestell-App', 'Schnittstelle'],
  },
  input: z
    .strictObject({ locationCode: zText(20).min(1, { error: 'Bitte den Standort nennen.' }), kind: zKind, from: zDay, to: zDay })
    .refine((input) => input.to >= input.from, { error: 'Das Ende liegt vor dem Beginn.', path: ['to'] })
    .refine((input) => (Date.parse(`${input.to}T00:00:00Z`) - Date.parse(`${input.from}T00:00:00Z`)) / 86_400_000 <= 13, { error: 'Höchstens 14 Tage auf einmal.', path: ['to'] }),
  output: z.object({ serverTime: z.date(), load: z.array(z.object({ day: z.string(), start: zHm, end: zHm, booked: z.int() })) }),
  permission: READ,
  risk: 'read',
  scope: 'record',
  publicApi: true,
  assistant: 'never',
})

export const channelCheckCoupon = defineContract({
  name: 'sales.channel.checkCoupon',
  title: 'Gutschein für einen Vertriebskanal prüfen',
  description: 'Prüft einen Gutscheincode live: gültig, Rabatt, Grund der Ablehnung – mit den Grenzen gesamt und je Kunde.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Prüft `code` für einen Warenwert (brutto, ohne Nebenposten) und eine Kundenart ' +
      '(`private` oder `business`) – mit `customerNumber` bzw. `email` auch die Grenze je Kunde. Liefert `valid`, den Grund (`unknown`, ' +
      '`inactive`, `not_yet_valid`, `expired`, `b2c_only`, `limit`, `customer_limit`, `min_order`), einen Text für die Kundschaft und den Rabatt. ' +
      '`min_order` ist nur eine Warnung (`valid: true`, Rabatt 0). Bucht nichts.',
    keywords: ['Gutschein prüfen', 'Rabattcode', 'Bestell-App', 'Schnittstelle', 'Checkout'],
  },
  input: z.strictObject({
    code: zText(40).min(1, { error: 'Bitte einen Code angeben.' }),
    customerNumber: zText(40).optional(),
    email: zText(200).optional(),
    itemsGrossCents: zCents(),
    customerType: z.enum(['private', 'business']),
    day: zDay.optional(),
  }),
  output: z.object({
    valid: z.boolean(),
    reason: z.enum(['unknown', 'inactive', 'not_yet_valid', 'expired', 'b2c_only', 'limit', 'customer_limit', 'min_order']).nullable(),
    message: z.string().nullable(),
    coupon: z.object({ id: zId, code: z.string(), kind: z.enum(['percent', 'fixed']), value: z.int(), minOrderCents: z.int().nullable() }).nullable(),
    discountCents: z.int(),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
  publicApi: true,
  assistant: 'never',
})

/** Webhook „Bestell-App“ (sales.channel.*): nur Auslöser – die App holt die Regeln mit sales.channel.checkoutRules. */
export const channelCheckoutRulesChanged = defineEvent({
  name: 'sales.channel.checkoutRulesChanged',
  title: 'Lieferregeln für einen Vertriebskanal geändert',
  payload: z.object({ version: z.string() }),
})
